import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { loginUser } from '../auth/authService';
import logo from "../assets/Dinelyt_Transparent.png";

const OwnerLogin: React.FC = () => {
  const [email, setEmailMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginUser(email, password);
      const normalizedRole = data?.role?.toLowerCase();

      login({ role: normalizedRole, token: data.token, email });

      if (["owner", "admin", "manager", "dining associate"].includes(normalizedRole)) {
        navigate('/restaurant/dashboard');
      } else {
        setError('Unauthorized role');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-950 px-4 py-6 text-gray-300">
      <div className="w-full max-w-sm rounded-2xl p-8 bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 shadow-xl relative overflow-hidden backdrop-blur-sm">

        {/* Subtle Grid */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />

        <div className="relative z-10">
          {/* Logo + Heading */}
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="Dinelyt Logo" className="w-56 sm:w-64 h-auto drop-shadow-[0_0_10px_rgba(251,146,60,0.6)] animate-pulse-slow" />

            <h1 className="text-3xl font-extrabold text-white mt-4 tracking-tight text-center">
              Welcome Back
            </h1>

            <p className="text-base text-gray-400 text-center mt-1">
              Sign in to your Dinelyt account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center p-3 text-sm font-semibold text-red-400 bg-red-900/40 rounded-lg border border-red-800 animate-fade-in">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
                </svg>
                {error}
              </div>
            )}

            {/* Email / Mobile */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-1">Email or Mobile</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmailMobile(e.target.value)}
                className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all text-base"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all text-base"
                required
              />
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-400 text-white py-3 rounded-lg font-bold text-lg shadow-lg hover:bg-orange-500 transform hover:-translate-y-0.5 active:scale-95 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Authenticating...' : 'Login'}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-8 text-center text-sm text-gray-500">
            New to Dinelyt?{' '}
            <button
              onClick={() => navigate('/restaurant/onboarding')}
              className="text-orange-400 hover:underline font-semibold"
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerLogin;
